<template>
  <div>
    <div>
      <NavigationBreadcrumb :items="breadcrumb_items"></NavigationBreadcrumb>
      <h3 class="mt-3">
        {{ $getVisibleNames("common.commune", false, "Comuna") }}
        <AlternativeNameButton
          :table_name="'common.commune'"
          :allows_crud="allows_crud"
        ></AlternativeNameButton>
      </h3>
      <div style="display: flex">
        <div class="col" style="text-align: left">
          <ButtonMedium
            v-can="'common.add_commune'"
            v-if="allows_crud"
            :tooltip_text="
              $t('reusable.common.addTooltip', {
                entity: $getVisibleNames('common.commune', false, 'Comuna'),
              })
            "
            :text_button="$t('reusable.common.add')"
            :click_button="() => $bvModal.show(`new-commune-modal`)"
            :icon="'plus'"
            :variant="'primary'"
          >
          </ButtonMedium>
        </div>
      </div>
      <GenericBTable
        :items="communes"
        :fields="communesFields"
        :filter="input_search"
        :filterCustom="filterCustom"
        @emitChangeSearchField="changeInputSearch"
        :show_pagination="true"
        :search_filter="true"
        :columns_display="true"
        :allows_crud="allows_crud"
        :display_id="display_id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
      >
        <template v-if="allows_crud" #cell(actions)="row">
          <ButtonSmall
            v-can="'common.change_commune'"
            :tooltip_text="
              $t('reusable.common.editTooltip', {
                entity: $getVisibleNames('common.commune', false, 'Comuna'),
              })
            "
            :click_button="
              () => $bvModal.show(`edit-commune-modal-${row.item.id}`)
            "
            :class="``"
            :icon="'square'"
          ></ButtonSmall>
          <ButtonSmall
            v-can="'common.delete_commune'"
            :click_button="() => askForDeleteCommune(row.item.id)"
            :tooltip_text="
              $t('reusable.common.deleteTooltip', {
                entity: $getVisibleNames('common.commune', false, 'Comuna'),
              })
            "
            :icon="'trash'"
          ></ButtonSmall>
          <b-modal
            :id="`edit-commune-modal-${row.item.id}`"
            :title="
              $t('reusable.common.editModalTitle', {
                entity: $getVisibleNames('common.commune', false, 'Comuna'),
              })
            "
            size="lg"
            hide-footer
          >
            <CommuneForm
              :Commune="row.item"
              @updated="slotUpdatedCommune"
              :show_title="false"
            ></CommuneForm>
          </b-modal>
        </template>
      </GenericBTable>
    </div>
    <b-modal
      :id="`new-commune-modal`"
      :hide-footer="true"
      :title="
        $t('reusable.common.createModalTitle', {
          entity: $getVisibleNames('common.commune', false, 'Comuna'),
        })
      "
      size="lg"
    >
      <CommuneForm @created="slotCreatedCommune"></CommuneForm>
    </b-modal>
  </div>
</template>
<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";

export default {
  name: "CommuneView",
  components: {
    CommuneForm: () =>
      import("@/components/mesh/CurricularResources/Commune/CommuneForm"),
    GenericBTable: () => import("@/components/reusable/GenericBTable"),
    AlternativeNameButton: () =>
      import("@/components/reusable/AlternativeNameButton"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
    NavigationBreadcrumb: () =>
      import("@/components/reusable/NavigationBreadcrumb"),
  },
  props: {
    display_id: {},
  },
  data() {
    return {
      sortBy: "",
      input_search: "",
      sortDesc: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      communes: names.COMMUNES,
      regions: names.REGIONS,
      countries: names.COUNTRIES,
    }),
    breadcrumb_items() {
      return [
        {
          text: this.$t("reusable.breadcrumb.home"),
          to: "/home",
        },
        {
          text: this.$getVisibleNames("common.commune", false, "Comuna"),
        },
      ];
    },
    allows_crud() {
      const has_permission = this.$hasPermissions("common.add_commune");
      if (has_permission) return has_permission;
      else return false;
    },
    communesFields() {
      if (!this.allows_crud)
        return this.communes_fields.filter(
          (field) => !field.requires_allows_crud
        );
      else return this.communes_fields;
    },
    communes_fields() {
      return [
        {
          key: "order",
          label: this.$t("reusable.common.order"),
          sortable: true,
          tdClass: "text-center",
          display_column: false,
        },
        {
          key: "name",
          label: String(
            this.$getVisibleNames("common.commune", true, "Comunas")
          ),
          sortable: true,
          tdClass: "text-left",
          display_column: true,
        },
        {
          key: "region",
          label: this.$getVisibleNames("common.region", true, "Región"),
          sortable: true,
          // La celda muestra el nombre de la región, no su id
          sortByFormatted: true,
          tdClass: "text-left",
          display_column: true,
          formatter: (value) => this.getRegionName(value),
        },
        {
          key: "actions",
          label: "",
          display_column: true,
          thClass: "noprint",
          tdClass: "noprint",
        },
      ];
    },
  },
  methods: {
    filterCustom(row) {
      if (
        this.$filtered(String(row.name), this.input_search) ||
        this.$filtered(row.order, this.input_search) ||
        this.$filtered(this.getRegionName(row.region), this.input_search)
      ) {
        return true;
      } else {
        false;
      }
    },
    changeInputSearch(input_search) {
      this.input_search = input_search;
    },
    getRegionName(region_id) {
      const region = this.regions.find((x) => x.id == region_id);
      return region ? region.name : "";
    },
    askForDeleteCommune(commune_id) {
      const entityName = this.$getVisibleNames(
        "common.commune",
        false,
        "Comuna"
      );
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle", {
          entity: entityName,
        }),
        text: this.$t("reusable.common.deleteConfirmText"),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.$store.dispatch(names.DELETE_COMMUNE, commune_id).then(() => {
            toast(
              String(entityName + " " + this.$t("reusable.common.deleted"))
            );
          });
        }
      });
    },
    slotCreatedCommune() {
      this.$bvModal.hide("new-commune-modal");
    },
    slotUpdatedCommune(commune) {
      this.$bvModal.hide(`edit-commune-modal-${commune.id}`);
    },
  },
  mounted() {
    this.$store.dispatch(names.FETCH_COUNTRIES);
    this.$store.dispatch(names.FETCH_REGIONS);
    this.$store.dispatch(names.FETCH_COMMUNES);
  },
  created() {},
};
</script>
<style scoped>
th {
  background-color: var(--primary-color);
  border-radius: 6px;
  box-shadow: 1px 1px 3px rgb(72, 72, 72);
}

.title-header {
  padding: 0;
  margin-top: auto;
}
.search-commune {
  max-width: 50rem;
  margin-left: auto;
  margin-right: 18.5%;
  margin-bottom: 1rem;
  margin-top: 0.5rem;
}
.edit-section {
  margin-top: -2%;
}
.campus-div {
  background-color: var(--primary-color);
  color: white;
  max-width: fit-content;
  border-radius: 3px;
  margin: 0.1em 0.1em;
  padding: 1px 4px;
  flex-grow: 1;
  font-size: var(--thirth-font-size);
  text-align: center;
}
.campus-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
}
</style>